import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { t } from "@/lib/localization";

type PhotoLookInput = {
    avatarId: string;
    name: string;
    assetId?: string;
    url?: string;
};

export const useAddPhotoLook = () => {
    const qc = useQueryClient();
    return useMutation<unknown, Error, PhotoLookInput>({
        mutationKey: ["addPhotoLook"],
        mutationFn: async ({ avatarId, ...rest }) => {
            const response = await client.api.avatars.heygen.avatars[":id"]["looks"]["photo"].$post(
                { param: { id: avatarId }, json: rest }
            );
            if (!response.ok) throw new Error("Failed to add look");
            return await response.json();
        },
        onSuccess: (_data, { avatarId }) => {
            toast.success(t("avatars.toast.add-look.success"), {
                keyName: "avatars.toast.add-look.success",
            });
            qc.invalidateQueries({ queryKey: ["avatars"] });
            qc.invalidateQueries({ queryKey: ["avatar", avatarId] });
        },
        onError: () => {
            toast.error(t("avatars.toast.add-look.error"), {
                keyName: "avatars.toast.add-look.error",
            });
        },
    });
};

type PromptLookInput = {
    avatarId: string;
    name: string;
    prompt: string;
    referenceImages?: { assetId?: string; url?: string }[];
};

export const useAddPromptLook = () => {
    const qc = useQueryClient();
    return useMutation<unknown, Error, PromptLookInput>({
        mutationKey: ["addPromptLook"],
        mutationFn: async ({ avatarId, ...rest }) => {
            const response = await client.api.avatars.heygen.avatars[":id"]["looks"][
                "prompt"
            ].$post({ param: { id: avatarId }, json: rest });
            if (!response.ok) throw new Error("Failed to add look");
            return await response.json();
        },
        onSuccess: (_data, { avatarId }) => {
            toast.success(t("avatars.toast.add-look-prompt.success"), {
                keyName: "avatars.toast.add-look-prompt.success",
            });
            qc.invalidateQueries({ queryKey: ["avatars"] });
            qc.invalidateQueries({ queryKey: ["avatar", avatarId] });
        },
        onError: () => {
            toast.error(t("avatars.toast.add-look-prompt.error"), {
                keyName: "avatars.toast.add-look-prompt.error",
            });
        },
    });
};
